/**
 * The first line of a `spawn_agent` result — written by the dispatcher
 * (`spawnAgentDispatch.ts`), read by the dashboard (`ToolCallDetails.tsx`).
 * One module for both sides, so the header can't change on one side only.
 */

export interface SubAgentHeader {
  /** The worker stopped at its step limit; the report is partial. */
  partial: boolean;
  /** The limit it stopped at (partial results only). */
  stepLimit?: number;
  toolCalls: number;
  /** Tokens processed over every model request of the run (cache reads included). */
  tokens: number;
  transcriptPath?: string;
}

const TRANSCRIPT = ' · full transcript (every tool call and result): ';

export function formatSubAgentHeader(h: SubAgentHeader): string {
  const status = h.partial
    ? `Sub-agent stopped at its step limit${h.stepLimit !== undefined ? ` (${h.stepLimit})` : ''} — PARTIAL report; to finish, spawn again with a narrower task or a larger max_turns`
    : 'Sub-agent completed';
  // Tokens are cumulative over the run's model requests (each step re-sends
  // the context, mostly cached) — a processing total, not the context size.
  const transcript = h.transcriptPath ? `${TRANSCRIPT}${h.transcriptPath}` : '';
  return `[${status} — ${h.toolCalls} tool calls, ${h.tokens} tokens processed${transcript}]`;
}

const HEADER = /^\[(Sub-agent (?:completed|stopped at its step limit)[^\]]*)\]\n*/;

/** The header and the report after it; `header` is undefined when the output has none. */
export function parseSubAgentResult(output: string): { header?: SubAgentHeader; report: string } {
  const m = output.match(HEADER);
  if (!m) return { report: output.trim() };
  const line = m[1] ?? '';
  const counts = line.match(/— (\d+) tool calls, (\d+) tokens processed/);
  const limit = line.match(/step limit \((\d+)\)/);
  const transcriptAt = line.indexOf(TRANSCRIPT);
  const header: SubAgentHeader = {
    partial: line.startsWith('Sub-agent stopped'),
    toolCalls: Number(counts?.[1] ?? 0),
    tokens: Number(counts?.[2] ?? 0),
    ...(limit ? { stepLimit: Number(limit[1]) } : {}),
    ...(transcriptAt >= 0 ? { transcriptPath: line.slice(transcriptAt + TRANSCRIPT.length) } : {}),
  };
  return { header, report: output.slice(m[0].length).trim() };
}
